import * as React from "react";
import { NavLink } from "react-router-dom";
import { hasAnyRole } from "../../utility/RoleService";
import "./RequireRole.css";

/**
 * Route-level role gate for the HR module (Phase 0).
 *
 * Wrap a route's element to require one of `roles`:
 *
 *   <Route path="/hr/payroll-runs" element={
 *     <RequireRole roles={HR_ROLES}>
 *       <Suspense fallback={<PayrollSkeleton />}><PayrollRuns /></Suspense>
 *     </RequireRole>
 *   } />
 *
 * This is a UI gate, not a security boundary — localStorage is user-editable.
 * Every HR endpoint must independently call Helpers::requireRole() server-side.
 * The purpose here is to stop an unauthorised page from rendering and firing
 * requests that would only be rejected anyway.
 *
 * Shows an explicit "no access" panel rather than redirecting: someone who
 * follows a deep link deserves to know why the page is empty, and a silent
 * bounce to /dashboard reads like a bug.
 *
 * Scope note: applied to NEW HR routes only. The ~2,000 existing routes are
 * deliberately untouched — retrofitting them would change access for users who
 * legitimately reach those screens today.
 */
export type RequireRoleProps = {
    roles: string[];
    children: JSX.Element;
    /** Optional override for the message shown when access is denied. */
    message?: string;
};

const IconLock = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="10" width="16" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 1 1 8 0v3" />
    </svg>
);

export default function RequireRole({ roles, children, message }: RequireRoleProps) {
    if (hasAnyRole(roles)) {
        return children;
    }

    return (
        <div className="rqr-page">
            <div className="rqr-card">
                <div className="rqr-badge">
                    <IconLock />
                </div>
                <h2 className="rqr-title">You don’t have access to this page</h2>
                <p className="rqr-sub">
                    {message ||
                        "This screen is restricted to the HR team. If you need access, ask your administrator to assign you the relevant role."}
                </p>
                <NavLink to="/dashboard" className="rqr-btn">
                    Back to Dashboard
                </NavLink>
            </div>
        </div>
    );
}
